/* eslint-disable max-len */
/* eslint-disable no-console */
import { takeLatest, put, select } from 'redux-saga/effects';
import moment from 'moment';
import {
  SEND_ATC_FROM_BUYBOX_EVENT_TO_ANALYTICS,
  SEND_PAYMENT_ESTIMATOR_EVENT_TO_ANALYTICS,
  PRICING_AVAILABLE,
  SEND_AVAILABILITY_MODAL_LOAD_EVENT_TO_ANALYTICS,
  SEND_CHECK_AVAILABILITY_EVENT_TO_ANALYTICS,
} from '../../actions/actionTypes';
import {
  cartOperationSuccess,
  setIsAppLoadAnalyticsDone,
} from '../../actions/cartActions/_actionCreators';
import safeSaga from '../safeSaga';
import { Utils } from '../../utils/analytics/sendAnalyticsEvent';
import { buildBaseEventData, customProductPipLoaded } from '../../utils/analytics';
import {
  configSelector,
  pricingSelector,
  productDetailsAttributeSelector,
  analyticsSelector,
  thdStoreInfoSelector,
  productDetailsSelector,
} from '../../selectors';

const atcFromBuyBoxAnalyticsEventPayloadFactory = (items, logEventsToConsole) => {
  if (logEventsToConsole) {
    console.log('itemToThdAnalyticsEvent =>', JSON.stringify(items));
  }

  const atcFromBuyBoxAnalyticsPayload = buildBaseEventData('stash', 'cart add location');
  atcFromBuyBoxAnalyticsPayload.eventMode = 'stash';
  atcFromBuyBoxAnalyticsPayload.stash = [];
  const item = {
    pageType: 'pip',
    section: 'buybox',
    sharedSection: 'n/a',
    target: 'atc',
    item: items.map((i) => ({
      index: '',
      productID: i.itemId,
    })),
  };
  atcFromBuyBoxAnalyticsPayload.stash.push(item);
  return atcFromBuyBoxAnalyticsPayload;
};

const paymentEstimatorEventPayloadFactory = () => {
  const paymentEstimatorEvent = buildBaseEventData(
    'button submission',
    'content module interaction'
  );
  const module = {
    name: 'buy box',
    feature: 'plcc-est',
    type: 'cta',
  };
  paymentEstimatorEvent.content = {};
  paymentEstimatorEvent.content.module = [module];
  return paymentEstimatorEvent;
};

const availabilityModalLoadEventPayloadFactory = () => {
  const availabilityModalLoadEvent = buildBaseEventData(
    'overlay',
    'check fulfillment availability'
  );
  return availabilityModalLoadEvent;
};
const isBackorderedOrAvailable = (availability) =>
  availability.every((avail) => avail.status === 'AVAILABLE') ||
  availability.some((avail) => avail.status === 'BACK_ORDERED');

/* this method currently checks for all available scenarios ,
should be modified once we start taking backorders and unavailable scenarios in account */
const isPayloadAnalyticsProof = (payload) => {
  if (payload?.deliveryAvailabilityResponse?.deliveryAvailability) {
    const earliestAvailabilityDate = moment(
      payload.deliveryAvailabilityResponse.deliveryAvailability.earliestAvailabilityDate,
      'YYYY-MM-DD',
      true
    );
    const { availability } = payload.deliveryAvailabilityResponse.deliveryAvailability;
    if (
      earliestAvailabilityDate &&
      earliestAvailabilityDate?.isValid() &&
      availability &&
      isBackorderedOrAvailable(availability)
    ) {
      return true;
    }
  }
  return false;
};
const checkAvailabilityEventPayloadFactory = (payload) => {
  const { availability, zipCode, earliestAvailabilityDate } =
    payload.deliveryAvailabilityResponse.deliveryAvailability;
  const createCheckAvailabilityEventPayloadPerSku = (skuInfo) => {
    const checkAvailabilityEventPayload = buildBaseEventData(
      'button submission',
      'check fulfillment availability'
    );
    checkAvailabilityEventPayload.item = [];
    const item = {
      productInfo: {
        sku: skuInfo.itemId,
        eta: {
          applStartDate: earliestAvailabilityDate, // might need revisit later once we implement unavailable scenarios
          applEndDate: earliestAvailabilityDate, // might need revisit later once we implement unavailable scenarios
        },
        availabilityStatus: {
          backOrder: skuInfo.status === 'BACK_ORDERED',
          inStockAppl: skuInfo.status === 'AVAILABLE',
        },
        isBundled: false,
        isPackage: false,
      },
      fulfillment: {
        method: 'appl',
        zip: zipCode,
      },
    };
    checkAvailabilityEventPayload.item.push(item);
    return checkAvailabilityEventPayload;
  };

  const checkAvailabilityEventPayloads = availability.map(
    createCheckAvailabilityEventPayloadPerSku
  );
  return checkAvailabilityEventPayloads;
};

function* handleAnalytics(action) {
  switch (action.type) {
    case PRICING_AVAILABLE: {
      const analytics = yield select(analyticsSelector);
      if (analytics && !analytics.isAppLoadAnalyticsDone) {
        const config = yield select(configSelector);
        const productDetailsAttributes = yield select(productDetailsAttributeSelector);
        const pricing = yield select(pricingSelector);
        const thdStoreInfo = yield select(thdStoreInfoSelector);
        const productDetails = yield select(productDetailsSelector);
        const sendPaymentEstimatorImpressionToAnalytics =
          productDetailsAttributes &&
          productDetailsAttributes.showFinance === 'Yes' &&
          pricing &&
          pricing.specialPrice > 299;
        customProductPipLoaded(
          config?.partnerProductId,
          config?.productName,
          config?.brand,
          config?.logAnalyticEventsToConsole,
          config?.omnitureInfo,
          sendPaymentEstimatorImpressionToAnalytics,
          thdStoreInfo,
          productDetails?.eta
        );
        yield put(setIsAppLoadAnalyticsDone(true));
      }
      break;
    }
    case SEND_ATC_FROM_BUYBOX_EVENT_TO_ANALYTICS: {
      const eventPayload = atcFromBuyBoxAnalyticsEventPayloadFactory(action.itemDetails);
      Utils.sendAnalyticsEvent(eventPayload);
      yield put(cartOperationSuccess(action.cartData, action.itemDetails));
      break;
    }

    case SEND_PAYMENT_ESTIMATOR_EVENT_TO_ANALYTICS: {
      const paymentEstimatorEvent = paymentEstimatorEventPayloadFactory();
      Utils.sendAnalyticsEvent(paymentEstimatorEvent);
      break;
    }

    case SEND_AVAILABILITY_MODAL_LOAD_EVENT_TO_ANALYTICS: {
      const availabilityModalLoadEventPayload = availabilityModalLoadEventPayloadFactory();
      Utils.sendAnalyticsEvent(availabilityModalLoadEventPayload);
      break;
    }

    case SEND_CHECK_AVAILABILITY_EVENT_TO_ANALYTICS: {
      if (isPayloadAnalyticsProof(action.payload)) {
        const checkAvailabilityEventPayloads = checkAvailabilityEventPayloadFactory(action.payload);
        if (checkAvailabilityEventPayloads && checkAvailabilityEventPayloads.length > 0) {
          checkAvailabilityEventPayloads.forEach((payload) => {
            Utils.sendAnalyticsEvent(payload);
          });
        }
      }

      break;
    }

    default:
      break;
  }
}

export default function* analyticsSaga() {
  yield takeLatest(
    [
      PRICING_AVAILABLE,
      SEND_ATC_FROM_BUYBOX_EVENT_TO_ANALYTICS,
      SEND_PAYMENT_ESTIMATOR_EVENT_TO_ANALYTICS,
      SEND_AVAILABILITY_MODAL_LOAD_EVENT_TO_ANALYTICS,
      SEND_CHECK_AVAILABILITY_EVENT_TO_ANALYTICS,
    ],
    safeSaga(handleAnalytics)
  );
}
